import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import "./LoginPage.css";
import APP_VERSION from "../version";

export default function YourCrewPage({ user, currentUser, setCurrentUser }) {
  const [crew, setCrew] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCamperForm, setShowCamperForm] = useState(false);
  const [showPetForm, setShowPetForm] = useState(false);
  const [newCamper, setNewCamper] = useState({ name: "", email: "", age: "" });
  const [newPet, setNewPet] = useState({ name: "", petType: "", breed: "" });
  const [firstVisit, setFirstVisit] = useState(true);
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [editedMember, setEditedMember] = useState({});

  const navigate = useNavigate();

  // 🔐 Redirect to login if no user
  useEffect(() => {
    if (!currentUser) {
      navigate("/login", { replace: true });
    }
  }, [currentUser, navigate]);

  // ✅ Handle logout
  const handleLogout = async () => {
    await fetch("/auth/logout", { credentials: "include" });
    setCurrentUser(null);
    navigate("/login", { replace: true });
  };

  const fetchCrew = async () => {
    try {
      const res = await fetch("/crew", { credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load crew");
      setCrew(data);
    } catch (err) {
      console.error("❌ Crew fetch error:", err);
      setError("Failed to load your crew");
    } finally {
      setLoading(false);
    }
  };

  const addCamper = async () => {
    if (!newCamper.name) return alert("Name is required");
    try {
      const res = await fetch("/crew", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...newCamper, type: "camper" })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to add camper");
      setCrew([...crew, data]);
      setNewCamper({ name: "", email: "", age: "" });
      setShowCamperForm(false);
    } catch (err) {
      console.error("❌ Add camper error:", err);
      setError("Could not add camper");
    }
  };

  const addPet = async () => {
    if (!newPet.name || !newPet.petType) return alert("Name and type are required");
    try {
      const res = await fetch("/crew", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newPet.name,
          breed: newPet.breed,
          pet_type: newPet.petType,
          type: "pet"
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to add pet");
      setCrew([...crew, data]);
      setNewPet({ name: "", petType: "", breed: "" });
      setShowPetForm(false);
    } catch (err) {
      console.error("❌ Add pet error:", err);
      setError("Could not add pet");
    }
  };

  const deleteMember = async (memberId) => {
    const confirmed = window.confirm("Are you sure you want to delete this member?");
    if (!confirmed) return;

    try {
      const res = await fetch(`/crew/${memberId}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to delete member");

      // Update the crew state to remove the deleted member
      setCrew((prevCrew) => prevCrew.filter((member) => member.id !== memberId));
    } catch (err) {
      console.error("❌ Delete member error:", err);
      setError("Could not delete member");
    }
  };

  useEffect(() => {
    fetchCrew();
    return () => setFirstVisit(false);
  }, []);

  const saveEditedMember = async () => {
    try {
      const res = await fetch(`/crew/${editingMemberId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editedMember)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to update member");

      setCrew(crew.map((m) => (m.id === editingMemberId ? data : m)));
      setEditingMemberId(null);
    } catch (err) {
      console.error("❌ Save edit error:", err);
      setError("Could not update member");
    }
  };

  const renderMemberBadge = (member, idx) => (
    <div
      key={member.id || idx}
      style={{
        backgroundColor: "#fff",
        color: "#2c7373",
        padding: "16px",
        borderRadius: "8px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
        position: "relative",
        textAlign: "left"
      }}
    >
      {member.email === user?.email && <strong style={{ color: "black" }}>Group Owner</strong>}

      {/* Edit Icon */}
      <img
        src="/edit-icon.png"
        alt="Edit"
        style={{
          position: "absolute",
          top: "12px",
          right: "12px",
          width: "20px",
          height: "20px",
          cursor: "pointer"
        }}
        onClick={() => {
          setEditingMemberId(member.id);
          setEditedMember({ ...member });
        }}
      />

      {/* Delete Icon */}
      <img
        src="/delete-icon.png"
        alt="Delete"
        style={{
          position: "absolute",
          top: "40px", // Adjust as necessary
          right: "12px",
          width: "20px",
          height: "20px",
          cursor: "pointer"
        }}
        onClick={() => {
          console.log(`Deleting member with ID: ${member.id}`); // Log for debugging
          deleteMember(member.id); // Handle delete action
        }}
      />
        {/* Render the member badge content */}
      {editingMemberId !== member.id ? (
        <>
          {/* Display images for pets or campers */}
          {member.type === "pet" && (
            <img
              src={member.pet_type === "Dog" ? "/pet-image.png" :
                    member.pet_type === "Horse" ? "/horse-image.png" :
                    member.pet_type === "Cat" ? "/cat-image.png" :
                    member.pet_type === "Velociraptor" ? "/velociraptor-image.png" :
                    "/dog-image.png"}
              alt="Pet"
              style={{
                position: "absolute",
                top: "30px",
                right: "40px",
                width: "60px",
                height: "60px",
                objectFit: "contain"
              }}
            />
          )}

          {member.type === "camper" && (
            <img
              src={member.age === "baby" ? "/baby-image.png" :
                    member.age === "toddler" ? "/toddler-image.png" :
                    member.age === "child" ? "/child-image.png" :
                    "/camper-image.png"}
              alt="Camper"
              style={{
                position: "absolute",
                top: "30px",
                right: "40px",
                width: "60px",
                height: "60px",
                objectFit: "contain"
              }}
            />
          )}

          <div><strong>Name:</strong> {member.name}</div>
          {member.type === "camper" && (
            <>
              <div><strong>Email:</strong> {member.email || ""}</div>
              <div><strong>Age:</strong> {member.age || ""}</div>
            </>
          )}
          {member.type === "pet" && (
            <>
              <div><strong>Pet Type:</strong> {member.pet_type || ""}</div>
              <div><strong>Breed:</strong> {member.breed || ""}</div>
            </>
          )}
        </>
      ) : (
        <div className="login-form" style={{ marginTop: "16px", padding: "20px", width: "100%", boxSizing: "border-box", backgroundColor: "#fff", borderRadius: "8px", boxShadow: "0 2px 6px rgba(0,0,0,0.2)" }}>
          {/* Editing form here */}
          <input
            type="text"
            placeholder="Name"
            value={editedMember.name || ""}
            onChange={(e) => setEditedMember({ ...editedMember, name: e.target.value })}
            style={{ border: "1px solid #ccc" }}
          />
          {member.type === "camper" && (
            <>
              <input
                type="email"
                placeholder="Email (optional)"
                value={editedMember.email || ""}
                onChange={(e) => setEditedMember({ ...editedMember, email: e.target.value })}
                style={{ border: "1px solid #ccc" }}
              />
              <select
                value={editedMember.age || ""}
                onChange={(e) => setEditedMember({ ...editedMember, age: e.target.value })}
                style={{ padding: "10px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "16px", color: editedMember.age ? "#000" : "#888" }}
              >
                <option value="" disabled>Select age</option>
                <option value="baby">Baby</option>
                <option value="toddler">Toddler</option>
                <option value="child">Child</option>
                <option value="adult">Adult</option>
              </select>
            </>
          )}
          {/* Handle pet data editing */}
          {member.type === "pet" && (
            <>
              <select
                value={editedMember.pet_type || ""}
                onChange={(e) => setEditedMember({ ...editedMember, pet_type: e.target.value })}
                style={{ padding: "10px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "16px", color: editedMember.pet_type ? "#000" : "#888" }}
              >
                <option value="" disabled>Select type</option>
                <option value="Dog">Dog</option>
                <option value="Horse">Horse</option>
                <option value="Cat">Cat</option>
                <option value="Velociraptor">Velociraptor</option>
              </select>
              <input
                type="text"
                placeholder="Breed (optional)"
                value={editedMember.breed || ""}
                onChange={(e) => setEditedMember({ ...editedMember, breed: e.target.value })}
                style={{ border: "1px solid #ccc" }}
              />
            </>
          )}
          <button type="button" onClick={saveEditedMember} className={`login-button ${editedMember.name ? "" : "disabled-button"}`} disabled={!editedMember.name}>Save</button>
          <button
            type="button"
            onClick={() => {
              setEditedMember({});
              setEditingMemberId(null);
            }}
            className="login-button"
            style={{
              marginTop: "8px",
              backgroundColor: "#ccc",
              color: "#333"
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="login-container">
      <div className="login-box">
        <div style={{ textAlign: "right", marginBottom: "10px" }}>
          <button
            onClick={() => navigate("/auth/logout")}
            style={{
              background: "none",
              color: "#4cafef",
              border: "none",
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "14px"
            }}
          >
            Logout
          </button>
        </div>

        <img
          src="/Crew-image.png"
          alt="Crew"
          style={{ width: "120px", margin: "0 auto 20px auto", display: "block" }}
        />
        <h2>Crew</h2>

        {loading && <p>Loading...</p>}
        {error && <div className="error-message">{error}</div>}

        <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "30px" }}>
          {crew.filter(m => m.type === "camper").map(renderMemberBadge)}
        </div>

        {/* Add Camper Form/Button */}
        <div className="login-form" style={{ marginTop: "20px" }}>
          <button type="button" onClick={() => setShowCamperForm(true)}>Add Camper</button>
          {showCamperForm && (
            <div className="login-form" style={{ backgroundColor: "#fff", borderRadius: "8px", boxShadow: "0 2px 6px rgba(0,0,0,0.2)", marginTop: "0", padding: "20px", width: "100%", boxSizing: "border-box" }}>
              <input type="text" placeholder="Name" value={newCamper.name} onChange={(e) => setNewCamper({ ...newCamper, name: e.target.value })} style={{ border: "1px solid #ccc" }} />
              <input type="email" placeholder="Email (optional)" value={newCamper.email} onChange={(e) => setNewCamper({ ...newCamper, email: e.target.value })} style={{ border: "1px solid #ccc" }} />
              <select value={newCamper.age} onChange={(e) => setNewCamper({ ...newCamper, age: e.target.value })} style={{ padding: "10px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "16px", color: newCamper.age ? "#000" : "#888" }}>
                <option value="" disabled>Select age</option>
                <option value="baby">Baby</option>
                <option value="toddler">Toddler</option>
                <option value="child">Child</option>
                <option value="adult">Adult</option>
              </select>
              <button type="button" onClick={addCamper} className={`login-button ${newCamper.name && newCamper.age ? "" : "disabled-button"}`} disabled={!newCamper.name || !newCamper.age}>Save Camper</button>
              <button
                type="button"
                onClick={() => setShowCamperForm(false)}
                className="login-button"
                style={{
                  marginTop: "8px",
                  backgroundColor: "#ccc",
                  color: "#333"
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Pet Badges */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "30px", marginBottom: "30px" }}>
          {crew.filter(m => m.type === "pet").map(renderMemberBadge)}
        </div>

        {/* Add Pet Form/Button */}
        <div className="login-form" style={{ marginTop: "20px" }}>
          <button type="button" onClick={() => setShowPetForm(true)}>Add Pet</button>
          {showPetForm && (
            <div className="login-form" style={{ backgroundColor: "#fff", borderRadius: "8px", boxShadow: "0 2px 6px rgba(0,0,0,0.2)", marginTop: "0", padding: "20px", width: "100%", boxSizing: "border-box" }}>
              <input type="text" placeholder="Name" value={newPet.name} onChange={(e) => setNewPet({ ...newPet, name: e.target.value })} style={{ border: "1px solid #ccc" }} />
              <select value={newPet.petType} onChange={(e) => setNewPet({ ...newPet, petType: e.target.value })} style={{ padding: "10px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "16px", color: newPet.petType ? "#000" : "#888" }}>
                <option value="" disabled>Select type</option>
                <option value="Dog">Dog</option>
                <option value="Horse">Horse</option>
                <option value="Cat">Cat</option>
                <option value="Velociraptor">Velociraptor</option>
              </select>
              <input type="text" placeholder="Breed (optional)" value={newPet.breed} onChange={(e) => setNewPet({ ...newPet, breed: e.target.value })} style={{ border: "1px solid #ccc" }} />
              <button type="button" onClick={addPet} className={`login-button ${newPet.name && newPet.petType ? "" : "disabled-button"}`} disabled={!newPet.name || !newPet.petType}>Save Pet</button>
              <button
                type="button"
                onClick={() => setShowPetForm(false)}
                className="login-button"
                style={{
                  marginTop: "8px",
                  backgroundColor: "#ccc",
                  color: "#333"
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        <button
          className="home-button"
          onClick={() => navigate("/")}
          style={{ marginTop: "30px", width: "100%" }}
        >
          Home
        </button>

        <div className="version-text" style={{marginTop: "20px", fontSize: "12px", color: "#ccc", display: "flex", justifyContent: "space-between" }}>
          <span>Version: <strong>{APP_VERSION}</strong></span>
          <div className="footer-links">
              <Link
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleLogout();
                }}
                className="footer-link"
              >
                Logout
              </Link>

              <Link to="/contact" className="footer-link">
                Contact us
              </Link>
            </div>
      </div>
      </div>
    </div>
  );
}
